const { localize } = require('@deriv-com/translations');
const BinaryPjax = require('./binary_pjax');
const pages_config = require('./binary_pages');
const Client = require('./client');
const Header = require('./header');
const NetworkMonitor = require('./network_monitor');
const Page = require('./page');
const BinarySocket = require('./socket');
const ContentVisibility = require('../common/content_visibility');
const DerivBanner = require('../common/deriv_banner');
const GTM = require('../../_common/base/gtm');
const Login = require('../../_common/base/login');
const LiveChat = require('../../_common/base/livechat');
const getElementById = require('../../_common/common_functions').getElementById;
const ScrollToAnchor = require('../../_common/scroll_to_anchor');
const isStorageSupported = require('../../_common/storage').isStorageSupported;
const ThirdPartyLinks = require('../../_common/third_party_links');
const urlFor = require('../../_common/url').urlFor;
const createElement = require('../../_common/utility').createElement;
const DevShortcuts = require('../../_common/dev_shortcuts');
const { getI18nInstance, isTranslationsInitialized } = require('../../_common/translation-init');
const { initDynamicConfig } = require('../../config');
const DerivV2Auth = require('../../services/deriv-v2-auth');
const Accounts = require('../../_common/base/accounts');
const DforgeUsage = require('../../_common/dforge_usage');

const BinaryLoader = (() => {
    let container;
    let active_script = null;
    // The first pjax load is the page_view startUsageTracking already sent.
    let is_first_page = true;

    const init = async () => {
        // Initialize dynamic config FIRST - handles white-label redirects and ensures App ID is correct
        // before any other logic runs (like Accounts parsing or URL checking).
        await initDynamicConfig();

        DforgeUsage.startUsageTracking();

        // Handle the OAuth 2.0 PKCE callback.
        const url_params = new URLSearchParams(window.location.search);
        const v2Code = url_params.get('code');
        const v2State = url_params.get('state');
        let shouldRedirectFromCallback = false;

        if (url_params.get('error') && /\/callback\/?$/i.test(window.location.pathname)) {
            DforgeUsage.trackUsage('trader_login_failed', {
                reason: url_params.get('error') === 'access_denied' ? 'declined' : 'provider_error',
            });
        }

        if (v2Code && v2State) {
            const result = await DerivV2Auth.completeAuthFlow(v2Code, v2State);

            if (result.success && result.accounts && result.accounts.length) {
                // Populate the existing UI account store from canonical v2 accounts.
                const accountsList = {};
                const clientAccounts = {};
                result.accounts.forEach((account) => {
                    accountsList[account.account_id] = account.account_id;
                    clientAccounts[account.account_id] = {
                        loginid   : account.account_id,
                        token     : account.account_id,
                        currency  : account.currency,
                        is_virtual: account.account_type === 'demo',
                    };
                });
                Accounts.setAccountsMap(accountsList);
                Accounts.setClientAccounts(clientAccounts);

                const first_account = result.accounts[0];
                localStorage.setItem('active_loginid', first_account.account_id);
                localStorage.setItem('account_id', first_account.account_id);
                localStorage.setItem('account_type', first_account.account_type);
                localStorage.removeItem('authToken');
                localStorage.setItem('current_account', JSON.stringify({
                    loginid   : first_account.account_id,
                    token     : first_account.account_id,
                    currency  : first_account.currency,
                    balance   : first_account.balance,
                    is_virtual: first_account.account_type === 'demo' ? 1 : 0,
                }));
                shouldRedirectFromCallback = true;
                DforgeUsage.trackUsage('trader_login_completed', { accounts: result.accounts.length });
            } else {
                DforgeUsage.trackUsage('trader_login_failed', {
                    reason: result.success ? 'no_accounts' : 'exchange_failed',
                });
                // eslint-disable-next-line no-console
                console.error('[SmartTrader] v2 auth flow failed:', result.error);
            }

            // Clean OAuth params from URL.
            const cleanUrl = new URL(window.location.href);
            cleanUrl.searchParams.delete('code');
            cleanUrl.searchParams.delete('state');
            window.history.replaceState({}, document.title, cleanUrl.toString());

        }

        if (shouldRedirectFromCallback && /\/callback\/?$/i.test(window.location.pathname)) {
            DforgeUsage.flushUsage();
            window.location.replace(Client.defaultRedirectUrl());
            return;
        }

        // Fetch conversion config on boot
        await Accounts.fetchConversionConfig();

        // Ensure translations are initialized
        if (!isTranslationsInitialized()) {
            // eslint-disable-next-line no-console
            console.warn('Translations not initialized, falling back to legacy system');
        }

        if (!isStorageSupported(localStorage) || !isStorageSupported(sessionStorage)) {
            Header.displayNotification({ key: 'storage_not_supported', title: 'Storage not supported', message: localize('Requires your browser\'s web storage to be enabled in order to function properly. Please enable it or exit private browsing mode.'), type: 'danger' });
            getElementById('btn_login').classList.add('button-disabled');
        }

        // Make i18n instance globally available
        const i18nInstance = getI18nInstance();
        if (i18nInstance) {
            window.smartTraderI18n = i18nInstance;
        }

        Page.showNotificationOutdatedBrowser();

        // Note: We no longer check whoami during initialization.
        // Token validation is handled by the WebSocket authorize response.
        // If authorize fails with an invalid token, socket_general.js handles the logout.

        Client.init();
        NetworkMonitor.init();
        DerivBanner.chooseBanner();
        container = getElementById('content-holder');
        container.addEventListener('binarypjax:before', beforeContentChange);
        window.addEventListener('beforeunload', beforeContentChange);
        container.addEventListener('binarypjax:after', afterContentChange);
        
        try {
            BinaryPjax.init(container, '#content');
        } catch (error) {
            // BinaryPjax initialization error - redirect to home
            window.location.replace(`${window.location.protocol}//${window.location.hostname}`);
        }
        
        ThirdPartyLinks.init();
        DevShortcuts.init();

    };

    const beforeContentChange = () => {
        if (active_script) {
            BinarySocket.removeOnDisconnect();
            BinarySocket.removeOnReconnect();
            if (typeof active_script.onUnload === 'function') {
                active_script.onUnload();
            }
            active_script = null;
        }

        ScrollToAnchor.cleanup();
    };

    const afterContentChange = (e) => {
        Page.onLoad();

        if (is_first_page) is_first_page = false;
        else DforgeUsage.trackUsage('page_view', { tab: DforgeUsage.currentTab() });

        const this_page = e.detail.getAttribute('data-page');
        if (Object.prototype.hasOwnProperty.call(pages_config, this_page)) {
            loadHandler(this_page);
        } else if (/\/get-started\//i.test(window.location.pathname)) {
            loadHandler('get-started');
        }

        // Make sure content is properly loaded or visible before scrolling to anchor.
        ContentVisibility.init().then(() => {
            // BinarySocket.wait('balance').then(() => {
            GTM.pushDataLayer({ event: 'page_load' });

            // reroute LiveChat group
            LiveChat.rerouteGroup();

            // first time load.
            const last_image = $('#content img').last();
            if (last_image) {
                last_image.on('load', () => {
                    ScrollToAnchor.init();
                });
            }

            ScrollToAnchor.init();

            ContentVisibility.centerAlignSelect(true);
            // });
        });

        if (active_script) {
            BinarySocket.setOnReconnect(active_script.onReconnect);
        }
    };

    const error_messages = {
        login            : () => localize('Please <a>log in</a> or <a href="{{signup_url}}">sign up</a> to view this page.', { signup_url: urlFor('new-account') }),
        only_virtual     : () => localize('Sorry, this feature is available to virtual accounts only.'),
        only_real        : () => localize('This feature is not relevant to virtual-money accounts.'),
        not_authenticated: () => localize('This page is only available to logged out clients.'),
        no_mf            : () => localize('Sorry, but binary options trading is not available in your financial account.'),
        offerings_blocked: () => localize('Sorry, options trading isn’t available in the United Kingdom and the Isle of Man.'),
        options_blocked  : () => localize('Sorry, but binary options trading is not available in your country.'),
    };

    const loadHandler = (this_page) => {
        const config = { ...pages_config[this_page] };
        active_script = config.module;
        if (config.is_authenticated) {
            if (!Client.isLoggedIn()) {
                displayMessage(error_messages.login());
            } else {
                BinarySocket.wait('balance')
                    .then((response) => {
                        if (response.error) {
                            displayMessage(error_messages.login());
                        } else if (config.only_virtual && !Client.get('is_virtual')) {
                            displayMessage(error_messages.only_virtual());
                        } else if (config.only_real && Client.get('is_virtual')) {
                            displayMessage(error_messages.only_real());
                        } else {
                            loadActiveScript();
                        }
                    });
            }
        } else if (config.not_authenticated && Client.isLoggedIn()) {
            if (this_page === 'home' || this_page === 'new-account') {
                BinaryPjax.load(`${Client.defaultRedirectUrl()}${window.location.search}`, true);
            } else {
                handleNotAuthenticated();
            }
        } else {
            loadActiveScript();
        }
        
        BinarySocket.wait('balance').then(() => {
            // Removed account type and regional restrictions - allow all account types
            // Original restrictions removed: isOptionsBlocked(), isOfferingBlocked(), isAccountOfType('financial')
        });

        BinarySocket.setOnDisconnect(active_script.onDisconnect);
    };

    const loadActiveScript = () => {
        if (active_script && typeof active_script.onLoad === 'function') {
            // Load script directly without waiting for website_status
            active_script.onLoad();
        }
    };

    const displayMessage = (localized_message) => {
        const content = container.querySelector('#content .container');
        if (!content) {
            return;
        }

        const div_container = createElement('div', { class: 'logged_out_title_container', html: Client.isOptionsBlocked() ? '' : content.getElementsByTagName('h1')[0] || '' });
        const div_notice = createElement('p', { class: 'center-text notice-msg', html: localized_message });

        div_container.appendChild(div_notice);

        content.html(div_container);

        const link = content.getElementsByTagName('a')[0];
        if (link) {
            link.addEventListener('click', () => { Login.redirectToLogin(); });
        }
    };

    // displayUnavailable function removed - no longer needed without account restrictions

    const handleNotAuthenticated = () => {
        const content = container.querySelector('#content');
        if (!content) {
            return;
        }
        content.classList.add('container');

        const outer_container = createElement('div', { class: 'logged_out_title_container' });
        outer_container.appendChild(container.querySelector('#page_info'));
        outer_container.appendChild(container.getElementsByTagName('h1')[0]);

        const rowDiv = (element) => {
            const row_element = createElement('div', { class: 'gr-padding-10' });
            row_element.appendChild(element);
            return row_element;
        };
        const inner_container = createElement('div', { class: 'center-text' });
        const error_msg = createElement('div', { class: 'center-text notice-msg', text: error_messages.not_authenticated() });
        const logout_cta = createElement('button');
        const logout_span = createElement('span', { text: localize('Sign out') });

        logout_cta.addEventListener('click', () => { Client.doLogout({ logout: 1 }); });
        logout_cta.appendChild(logout_span);
        inner_container.appendChild(rowDiv(error_msg));
        inner_container.appendChild(rowDiv(logout_cta));
        outer_container.append(inner_container);
        content.html(outer_container);
    };

    return {
        init,
    };
})();

module.exports = BinaryLoader;
